import { useRef, useCallback } from 'react';
import html2canvas from 'html2canvas';

interface UseDisintegrateOptions {
  onComplete?: () => void;
  duration?: number; // Animation duration in ms (default: 2000)
}

// Weighted random distribution - pixels near 'peak' position go to that canvas
function weightedRandom(peak: number, canvasCount: number): number {
  const weights: number[] = [];
  let totalWeight = 0;

  for (let i = 0; i < canvasCount; i++) {
    // Cubic falloff from peak position
    const weight = Math.pow(canvasCount - Math.abs(peak - i), 3);
    weights.push(weight);
    totalWeight += weight;
  }

  let random = Math.random() * totalWeight;
  for (let i = 0; i < canvasCount; i++) {
    random -= weights[i];
    if (random <= 0) return i;
  }
  return canvasCount - 1;
}

export function useDisintegrate(options: UseDisintegrateOptions = {}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const isDisintegrating = useRef(false);

  const disintegrate = useCallback(async () => {
    if (!containerRef.current || isDisintegrating.current) return;
    isDisintegrating.current = true;

    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const duration = options.duration ?? 2000;

    try {
      // Capture element
      const sourceCanvas = await html2canvas(container, {
        backgroundColor: null,
        scale: 1,
        useCORS: true,
        logging: false,
      });

      const width = sourceCanvas.width;
      const height = sourceCanvas.height;

      // Create particle container
      const particleContainer = document.createElement('div');
      particleContainer.style.cssText = `
        position: fixed;
        top: ${rect.top}px;
        left: ${rect.left}px;
        width: ${rect.width}px;
        height: ${rect.height}px;
        pointer-events: none;
        z-index: 9999;
        overflow: visible;
      `;
      document.body.appendChild(particleContainer);

      // Hide original content
      container.style.opacity = '0';
      container.style.transition = 'none';

      const sourceCtx = sourceCanvas.getContext('2d', { willReadFrequently: true });
      if (!sourceCtx) return;

      const imageData = sourceCtx.getImageData(0, 0, width, height);
      const pixels = imageData.data;

      // Create 32 layer canvases (like the reference implementation)
      const layerCount = 32;
      const layers: { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D }[] = [];

      for (let i = 0; i < layerCount; i++) {
        const layerCanvas = document.createElement('canvas');
        layerCanvas.width = width;
        layerCanvas.height = height;
        layerCanvas.style.cssText = `
          position: absolute;
          left: 0;
          top: 0;
          width: ${rect.width}px;
          height: ${rect.height}px;
          opacity: 1;
          transform: translate(0, 0) rotate(0deg);
          transition: none;
        `;
        const layerCtx = layerCanvas.getContext('2d');
        if (layerCtx) {
          layers.push({ canvas: layerCanvas, ctx: layerCtx });
          particleContainer.appendChild(layerCanvas);
        }
      }

      // Distribute pixels to layers based on horizontal position
      // Pixels on the right go to earlier layers (dissolve right-to-left)
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const i = (y * width + x) * 4;
          const alpha = pixels[i + 3];
          if (alpha < 10) continue; // Skip transparent

          // Map x position to peak layer (right side = layer 0, left = layer 31)
          const normalizedX = x / width;
          const peakLayer = Math.floor((1 - normalizedX) * (layerCount - 1));
          const targetLayer = weightedRandom(peakLayer, layerCount);

          // Copy this pixel to target layer
          const layer = layers[targetLayer];
          if (layer) {
            layer.ctx.fillStyle = `rgba(${pixels[i]},${pixels[i + 1]},${pixels[i + 2]},${alpha / 255})`;
            layer.ctx.fillRect(x, y, 1, 1);
          }
        }
      }

      // Force reflow
      void particleContainer.offsetHeight;

      // Animate layers with staggered timing
      requestAnimationFrame(() => {
        layers.forEach(({ canvas }, index) => {
          const delay = index * 50; // 50ms between each layer
          const layerDuration = 800 + Math.random() * 400;
          const moveX = 50 + Math.random() * 100;
          const moveY = -(50 + Math.random() * 100);
          const rotation = (Math.random() - 0.5) * 30;

          canvas.style.transition = `transform ${layerDuration}ms ease-out ${delay}ms, opacity ${layerDuration * 0.6}ms ease-in ${delay}ms, filter ${layerDuration}ms ease-out ${delay}ms`;
          canvas.style.transform = `translate(${moveX}px, ${moveY}px) rotate(${rotation}deg)`;
          canvas.style.opacity = '0';
          canvas.style.filter = 'blur(2px)';
        });
      });

      // Cleanup and callback
      setTimeout(() => {
        particleContainer.remove();
        options.onComplete?.();
      }, duration + 300);
    } catch (error) {
      console.error('Disintegrate effect failed:', error);
      options.onComplete?.();
    }
  }, [options]);

  return { containerRef, disintegrate };
}
